import React, { useCallback, useMemo, useState } from 'react';

import { Dimensions, LayoutChangeEvent, View } from 'react-native';

import { useTranslation } from 'react-i18next';
import Slider from 'react-native-slider';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import {
    PLAYER_PROGRESS_BAR,
    PLAYER_PROGRESS_COUNTER_LEFT,
    PLAYER_PROGRESS_COUNTER_RIGHT,
} from 'config/testID';

import { lightGrey, whiteTransparent } from 'utils/colors';
import { convertHMS } from 'utils/hms';
import { isPhone, isTabletOrWeb, isWeb } from 'utils/responsiveness';

import {
    PlayerControlsAccessLevel,
    accessIsDisabled,
    accessIsEnabled,
} from 'screens/PlayerModal/hooks/useTrickModeControl';
import { PlayerError } from 'screens/PlayerModal/types';

import Text, { ETextCategory, ETextColor } from 'components/Text';

import useBottomSliderProps from '../hooks/useBottomSliderProps';
import styles, { tabletEmbeddedProgressWidth } from './styles';

type Props = {
    playerError: PlayerError;
    isLive: boolean;
    isCatchup: boolean;
    isVod: boolean;
    isStartover: boolean;
    isNpvr: boolean;
    timeshiftModeOn: boolean;
    timeshiftPosition: number | null;
    timeshiftDuration: number | undefined;
    startoverAllowed: boolean;
    rewindAccessLevel: PlayerControlsAccessLevel;
    fastforwardAccessLevel: PlayerControlsAccessLevel;
    showOverlay: boolean;
    position: number;
    duration: number;
    horizontalIndent: number;
    livePosition?: number;
    airTimeStart?: number;
    airTimeEnd?: number;
    progressColor?: string;
    fullscreen?: boolean;
    embedded?: boolean;
    isSOTSPauseDisabled?: boolean;
    timeshiftIndicatorEnabled?: boolean;
    skipBackwardDisabled: boolean;
    skipForwardDisabled: boolean;
    onSlidingStart?(): void;
    onSlidingValueChanged?(progress: number): void;
    onSlidingComplete?(value: number): void;
    onSlidingCompleteSilently?(value: number): void;
};

const {
    ACCESSIBILITY: {
        PLAYER: { ACCESSIBILITY_PLAYER_PROGRESS_BAR },
    },
} = LANGUAGE_KEYS;

const THUMB_TOUCH_SIZE = isWeb
    ? { width: 28, height: 28 }
    : { width: 40, height: 40 };

function BottomOverlaySection({
    showOverlay = false,
    position = 0,
    duration = 0,
    livePosition,
    onSlidingStart,
    onSlidingValueChanged,
    onSlidingComplete,
    onSlidingCompleteSilently,
    airTimeStart,
    airTimeEnd,
    isLive,
    isStartover,
    isCatchup,
    isVod,
    isNpvr,
    timeshiftModeOn,
    timeshiftPosition,
    timeshiftDuration,
    progressColor,
    startoverAllowed,
    isSOTSPauseDisabled = false,
    playerError,
    fullscreen,
    embedded,
    timeshiftIndicatorEnabled,
    horizontalIndent,
    skipBackwardDisabled,
    skipForwardDisabled,
    rewindAccessLevel,
    fastforwardAccessLevel,
}: Props) {
    const { t } = useTranslation();
    const realPosition =
        isSOTSPauseDisabled && isLive && livePosition ? livePosition : position;
    const scrubberDisabled =
        (skipBackwardDisabled || accessIsDisabled(rewindAccessLevel)) &&
        (skipForwardDisabled || accessIsDisabled(fastforwardAccessLevel));

    const {
        errorExists,
        progressValue,
        minimumBufferPosition,
        maximumBufferPosition,
        maximumValue,
        handleSlidingStart,
        handleSlidingComplete,
    } = useBottomSliderProps({
        position: realPosition,
        duration,
        livePosition,
        airTimeStart,
        airTimeEnd,
        isLive,
        isCatchup,
        isVod,
        isNpvr,
        timeshiftModeOn,
        timeshiftPosition,
        timeshiftDuration,
        startoverAllowed,
        playerError,
        onSlidingStart,
        onSlidingComplete,
    });

    const isEmbeddedTabletPlayer = embedded && !fullscreen && isTabletOrWeb;
    const isEmbeddedMobilePlayer = embedded && !fullscreen && isPhone;
    const timeLeft = convertHMS(maximumValue - position, true);

    const [progressTextWidth, setProgressTextWidth] = useState(0);

    const progressTime = useMemo(() => {
        if (isStartover || isCatchup || isVod || isNpvr) {
            const time = convertHMS(position, true);

            return time || '00:00';
        }

        if (progressValue) {
            return convertHMS(progressValue, true);
        }

        return '';
    }, [position, progressValue, isStartover, isCatchup, isVod, isNpvr]);

    const progressTimeString = useMemo(() => {
        if ((isStartover && !progressTime) || errorExists) {
            return '00:00';
        }

        return progressTime;
    }, [errorExists, isStartover, progressTime]);

    const progressLeftString = useMemo(() => {
        if (errorExists) {
            return '00:00';
        }

        if (timeLeft && progressTimeString) {
            return `${timeLeft}`;
        }

        return '';
    }, [progressTimeString, errorExists, timeLeft]);

    const containerStyle = useMemo(() => {
        if (isEmbeddedTabletPlayer) {
            return styles.tabletEmbeddedContainer;
        }

        if (isEmbeddedMobilePlayer) {
            return styles.mobileEmbeddedContainer;
        }

        return styles.container;
    }, [isEmbeddedTabletPlayer, isEmbeddedMobilePlayer]);

    const handleProgressTextLayout = useCallback((e: LayoutChangeEvent) => {
        setProgressTextWidth(e.nativeEvent.layout.width + 5);
    }, []);

    const progressSliderWidth = useMemo(() => {
        if (isEmbeddedTabletPlayer) {
            return {
                width: tabletEmbeddedProgressWidth - progressTextWidth * 2,
            };
        }

        return {
            width:
                Math.max(
                    Dimensions.get('window').width,
                    Dimensions.get('window').height,
                ) -
                progressTextWidth * 2 -
                horizontalIndent * 2,
        };
    }, [isEmbeddedTabletPlayer, progressTextWidth, horizontalIndent]);

    return (
        <View
            style={[containerStyle, { opacity: showOverlay ? 1 : 0 }]}
            accessible
            accessibilityLabel={t(ACCESSIBILITY_PLAYER_PROGRESS_BAR)}
        >
            <View
                style={[
                    styles.sliderContainer,
                    isEmbeddedMobilePlayer &&
                        styles.phoneEmbeddedSliderContainer,
                ]}
                testID={PLAYER_PROGRESS_BAR}
            >
                {!isEmbeddedMobilePlayer && (
                    <Text
                        style={[styles.textLeft, { width: progressTextWidth }]}
                        category={ETextCategory.body2}
                        color={ETextColor.neutral}
                        numberOfLines={1}
                        testID={PLAYER_PROGRESS_COUNTER_RIGHT}
                    >
                        {progressTimeString}
                    </Text>
                )}
                <Slider
                    disabled={scrubberDisabled || errorExists}
                    seekBwdDisabled={
                        skipBackwardDisabled ||
                        !accessIsEnabled(rewindAccessLevel)
                    }
                    seekFwdDisabled={
                        skipForwardDisabled ||
                        !accessIsEnabled(fastforwardAccessLevel)
                    }
                    hideProgress={
                        (isLive && (!airTimeStart || !airTimeEnd)) ||
                        errorExists
                    }
                    value={Math.min(
                        maximumValue,
                        errorExists ? 0 : progressValue,
                    )}
                    minimumBufferPosition={minimumBufferPosition}
                    maximumBufferPosition={maximumBufferPosition}
                    maximumValue={maximumValue}
                    style={
                        isEmbeddedMobilePlayer
                            ? styles.mobileEmbeddedSlider
                            : [styles.slider, progressSliderWidth]
                    }
                    thumbStyle={styles.thumb}
                    thumbTouchSize={THUMB_TOUCH_SIZE}
                    minimumTrackTintColor={progressColor}
                    maximumTrackTintColor={whiteTransparent(0.2)}
                    bufferColor={lightGrey(0.2)}
                    onValueChange={onSlidingValueChanged}
                    onSlidingStart={handleSlidingStart}
                    onSlidingComplete={handleSlidingComplete}
                    onSlidingCompleteSilently={onSlidingCompleteSilently}
                    timeShiftIndicatorEnabled={timeshiftIndicatorEnabled}
                />
                {!isEmbeddedMobilePlayer && (
                    <Text
                        style={[styles.textRight, { width: progressTextWidth }]}
                        category={ETextCategory.body2}
                        color={ETextColor.neutral}
                        numberOfLines={1}
                        testID={PLAYER_PROGRESS_COUNTER_LEFT}
                    >
                        {progressLeftString}
                    </Text>
                )}
            </View>
            <Text
                style={styles.mockedProgressTime}
                category={ETextCategory.body2}
                color={ETextColor.neutral}
                numberOfLines={1}
                onLayout={handleProgressTextLayout}
            >
                00:00:00
            </Text>
        </View>
    );
}

export default BottomOverlaySection;
